// Personal Notes — a dedicated two-pane notes page with a rich TipTap editor.
// Routed at /{admin|hr|member}/notes. Notes are private to the authed user.
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { BulletList, TaskList, TaskItem } from "@tiptap/extension-list";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import {
  StickyNote,
  Plus,
  Pin,
  PinOff,
  Trash2,
  Search,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListMinus,
  ListTodo,
  TextQuote,
  Minus,
  Link2,
  Highlighter,
  Palette,
  Check,
  CalendarDays,
  Bell,
  BellOff,
  Share2,
  Users,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import {
  Page,
  PageHeader,
  PrimaryButton,
  SecondaryButton,
  ModalOverlay,
  Avatar,
} from "../../components/em/ui";
import { T } from "../../components/em/tokens";
import { notesApi, NOTE_COLORS } from "./notesApi";
import askPrompt from '../../components/em/askPrompt';

import { confirmDialog } from "../../components/em/confirm";
// ── helpers ────────────────────────────────────────────────────────────────
const stripHtml = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// "10 Jun 2026" — the automatic created-date tag stamped on every note.
const dateChip = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Server stores remind_at as 'YYYY-MM-DD HH:MM:SS' in UTC; optimistic local
// values are ISO strings. Normalise both into a local Date.
const parseUtc = (s) =>
  s ? new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z") : null;

// "11 Jun, 3:00 PM" — compact reminder timestamp for chips.
const reminderTime = (d) =>
  `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;

// Local Date → value for <input type="datetime-local"> (YYYY-MM-DDTHH:MM).
const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Bucket a note into a list group by recency of its last touch.
const groupOf = (iso) => {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.floor((startOfDay(now) - startOfDay(d)) / 86400000);
  if (dayDiff <= 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7) return "This week";
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth())
    return "This month";
  return "Earlier";
};
const GROUP_ORDER = [
  "Pinned",
  "Today",
  "Yesterday",
  "This week",
  "This month",
  "Earlier",
];

const colorValue = (note) => (note && note.color) || null;

// pinned-first, then most-recently updated — keeps list ordering consistent
// after optimistic pin/edit changes without re-fetching.
const sortNotes = (arr) =>
  [...arr].sort((a, b) => {
    if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
    return (
      new Date(b.updated_at || b.created_at || 0) -
      new Date(a.updated_at || a.created_at || 0)
    );
  });

// Text colors offered in the editor's palette (null = default ink).
const TEXT_COLORS = [
  { label: "Default", value: null, swatch: "#1f2328" },
  { label: "Orange", value: "#f54b01", swatch: "#f54b01" },
  { label: "Amber", value: "#d97706", swatch: "#d97706" },
  { label: "Green", value: "#00a63e", swatch: "#00a63e" },
  { label: "Blue", value: "#2b7fff", swatch: "#2b7fff" },
  { label: "Purple", value: "#8e51ff", swatch: "#8e51ff" },
  { label: "Red", value: "#e7000b", swatch: "#e7000b" },
  { label: "Gray", value: "#6a7282", swatch: "#6a7282" },
];

// ── editor styles (injected once) ───────────────────────────────────────────
const EDITOR_CSS = `
.notes-prose .ProseMirror { outline: none; min-height: 320px; font: 400 15px/1.7 ${T.font}; color: ${T.fg1}; }
.notes-prose .ProseMirror p { margin: 0 0 12px; }
.notes-prose .ProseMirror h1 { font: 800 26px/1.3 ${T.font}; margin: 20px 0 12px; color: ${T.fg1}; letter-spacing: -0.02em; }
.notes-prose .ProseMirror h2 { font: 700 20px/1.4 ${T.font}; margin: 18px 0 10px; color: ${T.fg1}; }
.notes-prose .ProseMirror h3 { font: 700 16px/1.45 ${T.font}; margin: 14px 0 8px; color: ${T.fg1}; }
/* index.css resets \`ol, ul { list-style: none }\` app-wide — put the markers
   back for plain lists. The [data-dash] and taskList variants below opt out
   again; they draw their own dash / checkbox. */
.notes-prose .ProseMirror ul, .notes-prose .ProseMirror ol { padding-left: 22px; margin: 0 0 12px; }
.notes-prose .ProseMirror ul { list-style: disc outside; }
.notes-prose .ProseMirror ol { list-style: decimal outside; }
.notes-prose .ProseMirror ul ul { list-style-type: circle; }
.notes-prose .ProseMirror ul ul ul { list-style-type: square; }
.notes-prose .ProseMirror ol ol { list-style-type: lower-alpha; }
.notes-prose .ProseMirror ol ol ol { list-style-type: lower-roman; }
.notes-prose .ProseMirror li { display: list-item; margin: 4px 0; }
.notes-prose .ProseMirror ul[data-dash] { list-style: none; padding-left: 8px; }
.notes-prose .ProseMirror ul[data-dash] > li { padding-left: 18px; position: relative; }
.notes-prose .ProseMirror ul[data-dash] > li::before {
  content: '\\2013'; position: absolute; left: 0; color: ${T.fg5}; font-weight: 600;
}
.notes-prose .ProseMirror ul[data-type="taskList"] { list-style: none; padding-left: 2px; }
.notes-prose .ProseMirror ul[data-type="taskList"] > li { display: flex; gap: 9px; align-items: flex-start; }
.notes-prose .ProseMirror ul[data-type="taskList"] > li > label { flex-shrink: 0; margin-top: 4px; }
.notes-prose .ProseMirror ul[data-type="taskList"] > li > label input {
  width: 15px; height: 15px; accent-color: ${T.orange}; cursor: pointer;
}
.notes-prose .ProseMirror ul[data-type="taskList"] > li > div { flex: 1; min-width: 0; }
.notes-prose .ProseMirror ul[data-type="taskList"] > li[data-checked="true"] > div {
  color: ${T.fg7}; text-decoration: line-through; text-decoration-color: ${T.fg7};
}
.notes-prose .ProseMirror blockquote {
  border-left: 3px solid ${T.orange}; margin: 0 0 12px; padding: 4px 0 4px 14px;
  color: ${T.fg4}; font-style: italic; background: ${T.orange50}; border-radius: 0 ${T.rSm}px ${T.rSm}px 0;
}
.notes-prose .ProseMirror blockquote p { margin: 0; }
.notes-prose .ProseMirror hr {
  border: none; border-top: 2px solid ${T.divider}; margin: 18px 0; border-radius: 2px;
}
.notes-prose .ProseMirror a { color: ${T.orange}; text-decoration: underline; }
.notes-prose .ProseMirror mark { background: #fff3a3; padding: 0 2px; border-radius: 3px; }
.notes-prose .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder); color: ${T.fg7}; float: left; height: 0; pointer-events: none;
}
`;

// Bullet list with a "dash" display variant (rendered as data-dash, styled in CSS).
const DashableBulletList = BulletList.extend({
  addAttributes() {
    return {
      ...(this.parent ? this.parent() : {}),
      dash: {
        default: false,
        parseHTML: (el) => el.getAttribute("data-dash") === "true",
        renderHTML: (attrs) => (attrs.dash ? { "data-dash": "true" } : {}),
      },
    };
  },
});

// ── toolbar button ───────────────────────────────────────────────────────────
const TbBtn = ({ icon: Icon, active, onClick, title }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    style={{
      width: 30,
      height: 30,
      borderRadius: T.rSm,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `1px solid ${active ? T.orangeEdge : "transparent"}`,
      background: active ? T.orangeSoft : "transparent",
      color: active ? T.orange : T.fg4,
      padding: 0,
    }}
  >
    <Icon size={15} />
  </button>
);

const TbDivider = () => (
  <span
    style={{ width: 1, height: 18, background: T.border, margin: "0 4px" }}
  />
);

// Text-color palette popover for the toolbar.
function ColorTbBtn({ editor }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const current = editor.getAttributes("textStyle").color || null;

  const pick = (value) => {
    const chain = editor.chain().focus();
    if (value) chain.setColor(value).run();
    else chain.unsetColor().run();
    setOpen(false);
  };

  return (
    <span
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex" }}
    >
      <TbBtn
        icon={Palette}
        title="Text color"
        active={!!current || open}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: 34,
            left: 0,
            zIndex: 30,
            display: "flex",
            gap: 6,
            padding: 8,
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd,
            boxShadow: T.shadowMd || T.shadowSm,
          }}
        >
          {TEXT_COLORS.map((c) => {
            const active = (c.value || null) === current;
            return (
              <button
                key={c.label}
                type="button"
                title={c.label}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(c.value);
                }}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: T.pill,
                  cursor: "pointer",
                  background: c.swatch,
                  border: `2px solid ${active ? T.fg1 : "#fff"}`,
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {active && <Check size={11} color="#fff" />}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

// ── rich TipTap editor ────────────────────────────────────────────────────────
function NoteEditor({ noteId, initialBody, onChange, readOnly = false }) {
  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: false, // replaced by DashableBulletList below
        link: { openOnClick: false, autolink: true },
      }),
      DashableBulletList,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      Highlight,
      Placeholder.configure({ placeholder: "Start writing your note…" }),
    ],
    content: initialBody || "",
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Load the selected note's body when the note changes (avoid clobbering edits).
  useEffect(() => {
    if (!editor) return;
    const next = initialBody || "";
    if (editor.getHTML() !== next) {
      editor.commands.setContent(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, editor]);

  // Shared (read-only) notes must never accept edits.
  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  if (!editor) return null;

  const setLink = async () => {
    const prev = editor.getAttributes("link").href || "";
    const url = await askPrompt("Link URL", prev, { placeholder: 'https://' });
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  // Plain bullets and dash bullets share the bulletList node; the dash variant
  // is just the same list with data-dash set, so the toggles convert in place.
  const toggleBullets = () => {
    const chain = editor.chain().focus();
    if (editor.isActive("bulletList", { dash: true }))
      chain.updateAttributes("bulletList", { dash: false }).run();
    else chain.toggleBulletList().run();
  };
  const toggleDashList = () => {
    const chain = editor.chain().focus();
    if (editor.isActive("bulletList", { dash: true }))
      chain.toggleBulletList().run();
    else if (editor.isActive("bulletList"))
      chain.updateAttributes("bulletList", { dash: true }).run();
    else
      chain
        .toggleBulletList()
        .updateAttributes("bulletList", { dash: true })
        .run();
  };

  return (
    <div
      className="notes-prose"
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {!readOnly && (
        <div
          style={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            padding: "8px 4px",
            borderBottom: `1px solid ${T.divider}`,
            marginBottom: 12,
          }}
        >
          <TbBtn
            icon={Bold}
            title="Bold"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <TbBtn
            icon={Italic}
            title="Italic"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <TbBtn
            icon={UnderlineIcon}
            title="Underline"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          <TbBtn
            icon={Strikethrough}
            title="Strikethrough"
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          />
          <TbDivider />
          <TbBtn
            icon={Heading1}
            title="Heading 1"
            active={editor.isActive("heading", { level: 1 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
          />
          <TbBtn
            icon={Heading2}
            title="Heading 2"
            active={editor.isActive("heading", { level: 2 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />
          <TbBtn
            icon={Heading3}
            title="Heading 3"
            active={editor.isActive("heading", { level: 3 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          />
          <TbDivider />
          <TbBtn
            icon={List}
            title="Bullet list"
            active={editor.isActive("bulletList", { dash: false })}
            onClick={toggleBullets}
          />
          <TbBtn
            icon={ListOrdered}
            title="Numbered list"
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <TbBtn
            icon={ListMinus}
            title="Dash list"
            active={editor.isActive("bulletList", { dash: true })}
            onClick={toggleDashList}
          />
          <TbBtn
            icon={ListTodo}
            title="Checklist"
            active={editor.isActive("taskList")}
            onClick={() => editor.chain().focus().toggleTaskList().run()}
          />
          <TbDivider />
          <TbBtn
            icon={TextQuote}
            title="Quote"
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          />
          <TbBtn
            icon={Minus}
            title="Divider"
            active={false}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          />
          <TbDivider />
          <TbBtn
            icon={Link2}
            title="Link"
            active={editor.isActive("link")}
            onClick={setLink}
          />
          <TbBtn
            icon={Highlighter}
            title="Highlight"
            active={editor.isActive("highlight")}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          />
          <ColorTbBtn editor={editor} />
        </div>
      )}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          paddingTop: readOnly ? 10 : 0,
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

// ── date tag chip ─────────────────────────────────────────────────────────────
const DateTag = ({ iso, subtle }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "2px 8px",
      borderRadius: T.pill,
      background: subtle ? T.bgHead : T.orange50,
      border: `1px solid ${subtle ? T.border : T.orangeEdge}`,
      font: `600 10.5px ${T.font}`,
      color: subtle ? T.fg5 : T.orange,
      whiteSpace: "nowrap",
    }}
  >
    <CalendarDays size={11} />
    {dateChip(iso)}
  </span>
);

// ── reminder chip ─────────────────────────────────────────────────────────────
// Pill shown on list cards and in the editor header when remind_at is set.
// Upcoming reminders get the orange tint; fired/past ones go muted grey.
function ReminderChip({ note }) {
  const d = parseUtc(note.remind_at);
  if (!d || Number.isNaN(d.getTime())) return null;
  const done = !!note.reminder_fired || d.getTime() < Date.now();
  const Icon = done ? BellOff : Bell;
  return (
    <span
      title={done ? "Reminder fired" : "Reminder scheduled"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: T.pill,
        background: done ? T.bgMuted : T.orange50,
        border: `1px solid ${done ? T.border : T.orangeEdge}`,
        font: `600 10.5px ${T.font}`,
        color: done ? T.fg6 : T.orange,
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={11} />
      {reminderTime(d)}
    </span>
  );
}

// ── reminder control (footer button + popover) ───────────────────────────────
// Same open/close pattern as the toolbar color popover, but anchored upward
// since it lives in the footer toolbar.
function ReminderControl({ note, onSave }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = () => {
    if (!open) {
      const d = parseUtc(note.remind_at);
      setValue(d && !Number.isNaN(d.getTime()) ? toLocalInput(d) : "");
    }
    setOpen((v) => !v);
  };

  const apply = (date) => {
    onSave(date ? date.toISOString() : null);
    setOpen(false);
  };

  const presets = [
    { label: "In 1 hour", make: () => new Date(Date.now() + 3600000) },
    {
      label: "Tomorrow 9 AM",
      make: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
    {
      label: "Next Monday 9 AM",
      make: () => {
        const d = new Date();
        d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
  ];

  const hasReminder = !!note.remind_at;

  return (
    <span
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex" }}
    >
      <FooterBtn
        icon={Bell}
        tone={hasReminder ? "active" : "default"}
        onClick={toggle}
      >
        {hasReminder ? "Reminder set" : "Remind me"}
      </FooterBtn>
      {open && (
        <div
          style={{
            position: "absolute",
            bottom: 40,
            left: 0,
            zIndex: 30,
            width: 250,
            padding: 12,
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd,
            boxShadow: T.shadowMd || T.shadowSm,
            display: "flex",
            flexDirection: "column",
            gap: 9,
          }}
        >
          <div style={{ font: `600 12px ${T.font}`, color: T.fg3 }}>
            Remind me at
          </div>
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "7px 9px",
              border: `1px solid ${T.border}`,
              borderRadius: T.rSm,
              font: `400 12.5px ${T.font}`,
              color: T.fg1,
              outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => apply(p.make())}
                style={{
                  padding: "4px 9px",
                  borderRadius: T.pill,
                  cursor: "pointer",
                  border: `1px solid ${T.border}`,
                  background: T.bgHead,
                  color: T.fg4,
                  font: `500 11px ${T.font}`,
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {hasReminder && (
              <button
                type="button"
                onClick={() => apply(null)}
                style={{
                  padding: 0,
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: T.fg5,
                  font: `500 12px ${T.font}`,
                  textDecoration: "underline",
                }}
              >
                Clear reminder
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button
              type="button"
              disabled={!value}
              onClick={() => value && apply(new Date(value))}
              style={{
                padding: "6px 12px",
                borderRadius: T.rSm,
                border: 0,
                background: value ? T.orange : T.bgMuted,
                color: value ? "#fff" : T.fg7,
                font: `600 12px ${T.font}`,
                cursor: value ? "pointer" : "default",
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

// ── share modal ───────────────────────────────────────────────────────────────
// Manage who a note is shared with: current recipients (removable) plus a
// searchable checkbox list of colleagues to add.
function ShareModal({ note, onClose, onCountChange }) {
  const [colleagues, setColleagues] = useState([]);
  const [shares, setShares] = useState([]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cols, current] = await Promise.all([
          notesApi.listColleagues(),
          notesApi.getShares(note.id),
        ]);
        if (!alive) return;
        setColleagues(cols);
        setShares(current);
      } catch (e) {
        console.error("Failed to load sharing data", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [note.id]);

  const recipientIds = useMemo(
    () => new Set(shares.map((s) => s.user_id)),
    [shares],
  );

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return colleagues.filter(
      (c) =>
        !recipientIds.has(c.id) &&
        (!q ||
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q)),
    );
  }, [colleagues, recipientIds, query]);

  const togglePick = (id) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const doShare = async () => {
    if (!picked.length || busy) return;
    setBusy(true);
    try {
      await notesApi.share(note.id, picked);
      const current = await notesApi.getShares(note.id);
      setShares(current);
      setPicked([]);
      onCountChange(current.length);
    } catch (e) {
      console.error("Share failed", e);
    } finally {
      setBusy(false);
    }
  };

  const removeShare = async (userId) => {
    try {
      await notesApi.unshare(note.id, userId);
      const next = shares.filter((s) => s.user_id !== userId);
      setShares(next);
      onCountChange(next.length);
    } catch (e) {
      console.error("Unshare failed", e);
    }
  };

  const sectionLabel = {
    font: `700 10.5px ${T.font}`,
    color: T.fg6 || T.fg5,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 8,
  };
  const personRow = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 8px",
    borderRadius: T.rMd,
  };
  const personName = {
    font: `600 13px/1.3 ${T.font}`,
    color: T.fg1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const personEmail = {
    font: `400 11.5px/1.3 ${T.font}`,
    color: T.fg5,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: "100%",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--em-bg-card)",
          borderRadius: T.rXl,
          overflow: "hidden",
          boxShadow: T.shadowMd || T.shadowSm,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "16px 22px",
            borderBottom: `1px solid ${T.divider}`,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: T.rLg,
              background: T.orange50,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Share2 size={16} color={T.orange} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
              Share note
            </div>
            <div style={{ ...personEmail, marginTop: 1 }}>
              {note.title || "Untitled note"}
            </div>
          </div>
          <button
            type="button"
            title="Close"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: T.rSm,
              border: `1px solid ${T.border}`,
              background: "transparent",
              color: T.fg4,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={13} />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "16px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {loading ? (
            <div
              style={{
                padding: 20,
                font: `400 13px ${T.font}`,
                color: T.fg5,
                textAlign: "center",
              }}
            >
              Loading…
            </div>
          ) : (
            <>
              {shares.length > 0 && (
                <div>
                  <div style={sectionLabel}>Shared with</div>
                  {shares.map((s) => (
                    <div key={s.user_id} style={personRow}>
                      <Avatar name={s.name} size={28} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={personName}>{s.name}</div>
                        <div style={personEmail}>{s.email}</div>
                      </div>
                      <button
                        type="button"
                        title="Remove access"
                        onClick={() => removeShare(s.user_id)}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: T.rSm,
                          cursor: "pointer",
                          border: "1px solid rgba(231,0,11,0.2)",
                          background: T.danger50,
                          color: T.danger,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <div style={sectionLabel}>Add people</div>
                <div style={{ position: "relative" }}>
                  <Search
                    size={14}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: T.fg7,
                    }}
                  />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name or email…"
                    style={{
                      width: "100%",
                      padding: "8px 12px 8px 32px",
                      boxSizing: "border-box",
                      border: `1px solid ${T.border}`,
                      borderRadius: T.rMd,
                      background: "var(--em-bg-card)",
                      font: `400 13px ${T.font}`,
                      color: T.fg1,
                      outline: "none",
                    }}
                  />
                </div>
                <div
                  style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}
                >
                  {candidates.length === 0 ? (
                    <div
                      style={{
                        padding: "14px 8px",
                        font: `400 12.5px ${T.font}`,
                        color: T.fg5,
                      }}
                    >
                      {query
                        ? "No colleagues match your search."
                        : "Everyone available already has access."}
                    </div>
                  ) : (
                    candidates.map((c) => {
                      const on = picked.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          style={{
                            ...personRow,
                            cursor: "pointer",
                            background: on ? T.orange50 : "transparent",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => togglePick(c.id)}
                            style={{
                              width: 15,
                              height: 15,
                              accentColor: T.orange,
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          />
                          <Avatar name={c.name} size={28} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={personName}>{c.name}</div>
                            <div style={personEmail}>{c.email}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 22px",
            borderTop: `1px solid ${T.divider}`,
            background: T.bgHead,
          }}
        >
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          <PrimaryButton
            onClick={doShare}
            disabled={!picked.length || busy}
            style={
              !picked.length || busy ? { opacity: 0.55, cursor: "default" } : {}
            }
          >
            {busy
              ? "Sharing…"
              : `Share${picked.length ? ` (${picked.length})` : ""}`}
          </PrimaryButton>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── list card ─────────────────────────────────────────────────────────────────
function NoteCard({ note, selected, onClick }) {
  const bar = colorValue(note) || T.border;
  const snippet = stripHtml(note.body) || "No additional text";
  const isShared = !!note.shared; // a note someone shared with me (read-only)
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        // height/line-height reset overrides the global `button { height:40px }`
        // rule in index.css that would otherwise clamp + clip the card.
        height: "auto",
        lineHeight: 1.3,
        position: "relative",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        gap: 10,
        padding: "12px 12px 12px 16px",
        marginBottom: 8,
        borderRadius: T.rLg,
        overflow: "hidden",
        border: `1px solid ${selected ? T.orangeEdge : T.border}`,
        background: selected ? T.orange50 : T.bgCard,
        boxShadow: selected ? "none" : T.shadowSm,
      }}
    >
      <span
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: bar,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {note.pinned && (
            <Pin size={12} color={T.orange} style={{ flexShrink: 0 }} />
          )}
          <span
            style={{
              font: `600 13px/1.3 ${T.font}`,
              color: T.fg1,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {note.title || "Untitled note"}
          </span>
        </div>
        <div
          style={{
            font: `400 12px/1.45 ${T.font}`,
            color: T.fg5,
            marginTop: 3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {snippet}
        </div>
        {/* meta row — only rendered when there's a badge / attribution to show */}
        {(isShared ||
          (!isShared && (note.remind_at || note.shared_count > 0))) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 7,
            }}
          >
            {!isShared && note.remind_at && <ReminderChip note={note} />}
            {!isShared && note.shared_count > 0 && (
              <span
                title={`Shared with ${note.shared_count}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  font: `600 11px ${T.font}`,
                  color: T.fg5,
                }}
              >
                <Share2 size={11} />
                {note.shared_count}
              </span>
            )}
            {isShared && (
              <span
                style={{
                  font: `500 11px ${T.font}`,
                  color: T.fg5,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                by {note.owner_name}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

// ── color picker (dropdown) ──────────────────────────────────────────────────
// Footer-matching pill trigger (current swatch + label + chevron) that opens an
// upward swatch-grid popover. Opens upward because it lives in the bottom
// toolbar; closes on outside click or Escape.
function ColorPicker({ value, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current =
    NOTE_COLORS.find((c) => (c.value || null) === (value || null)) ||
    NOTE_COLORS[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Note colour"
        aria-haspopup="true"
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          cursor: "pointer",
          padding: "7px 12px",
          borderRadius: T.rMd,
          border: `1px solid ${open ? T.orangeEdge : T.border}`,
          background: open ? T.orangeSoft : "#fff",
          color: T.fg4,
          font: `500 13px/1 ${T.font}`,
        }}
      >
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: T.pill,
            flexShrink: 0,
            background: current.swatch,
            border: current.value ? "none" : `1px solid ${T.border}`,
          }}
        />
        {current.label}
        <ChevronDown
          size={13}
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: 0,
            zIndex: 20,
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: T.rLg,
            boxShadow: T.shadowMd || T.shadowSm,
            padding: 10,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 8,
            width: "max-content",
          }}
        >
          {NOTE_COLORS.map((c) => {
            const active = (c.value || null) === (value || null);
            return (
              <button
                key={c.key}
                type="button"
                title={c.label}
                onClick={() => {
                  onPick(c.value);
                  setOpen(false);
                }}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: T.pill,
                  cursor: "pointer",
                  background: c.swatch,
                  border: c.value
                    ? `2px solid ${active ? T.fg1 : "#fff"}`
                    : `1px solid ${T.border}`,
                  boxShadow: active ? T.shadowSm : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {active && <Check size={13} color={c.value ? "#fff" : T.fg4} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── footer action button ──────────────────────────────────────────────────────
const FooterBtn = ({ icon: Icon, children, onClick, tone = "default" }) => {
  const tones = {
    default: { color: T.fg4, border: T.border, bg: "#fff" },
    active: { color: T.orange, border: T.orangeEdge, bg: T.orangeSoft },
    danger: { color: T.danger, border: "rgba(231,0,11,0.2)", bg: T.danger50 },
  };
  const t = tones[tone] || tones.default;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        padding: "7px 12px",
        borderRadius: T.rMd,
        border: `1px solid ${t.border}`,
        background: t.bg,
        color: t.color,
        font: `500 13px/1 ${T.font}`,
      }}
    >
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );
};

// ── page ───────────────────────────────────────────────────────────────────────
export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [sharedNotes, setSharedNotes] = useState([]); // notes shared with me (read-only)
  const [sharedOpen, setSharedOpen] = useState(true); // "Shared with me" group collapse
  const [shareOpen, setShareOpen] = useState(false); // share modal for the selected note
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [searchParams, setSearchParams] = useSearchParams();

  const titleRef = useRef(null);
  const saveTimer = useRef(null);
  const savedTimer = useRef(null);
  const draftRef = useRef({}); // { title, body } latest draft for the selected note
  const didInitSelect = useRef(false);

  // Initial load + deep-link selection.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [data, shared] = await Promise.all([
          notesApi.list(),
          notesApi.listShared().catch(() => []), // sharing must never block my own notes
        ]);
        if (!alive) return;
        const sorted = sortNotes(data);
        setNotes(sorted);
        setSharedNotes(shared);
        if (!didInitSelect.current) {
          didInitSelect.current = true;
          const deepShared = searchParams.get("shared");
          const sharedMatch =
            deepShared &&
            shared.find((n) => String(n.id) === String(deepShared));
          if (sharedMatch) {
            setSelectedId(sharedMatch.id);
          } else {
            const deep = searchParams.get("note");
            const match =
              deep && sorted.find((n) => String(n.id) === String(deep));
            setSelectedId(match ? match.id : sorted[0] ? sorted[0].id : null);
          }
        }
      } catch (e) {
        console.error("Failed to load notes", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () =>
      notes.find((n) => n.id === selectedId) ||
      sharedNotes.find((n) => n.id === selectedId) ||
      null,
    [notes, sharedNotes, selectedId],
  );
  const isShared = !!(selected && selected.shared); // shared notes are read-only for me

  // Client-side filter over loaded notes (title + plain-text body).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter(
      (n) =>
        (n.title || "").toLowerCase().includes(q) ||
        stripHtml(n.body).toLowerCase().includes(q),
    );
  }, [notes, query]);

  // Same filter over "Shared with me" (also matches the owner's name).
  const filteredShared = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sharedNotes;
    return sharedNotes.filter(
      (n) =>
        (n.title || "").toLowerCase().includes(q) ||
        (n.owner_name || "").toLowerCase().includes(q) ||
        stripHtml(n.body).toLowerCase().includes(q),
    );
  }, [sharedNotes, query]);

  // Group the (already pinned-first, recency-sorted) list under date headers.
  const grouped = useMemo(() => {
    const buckets = new Map();
    filtered.forEach((n) => {
      const key = n.pinned ? "Pinned" : groupOf(n.updated_at || n.created_at);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(n);
    });
    return GROUP_ORDER.filter((g) => buckets.has(g)).map((g) => [
      g,
      buckets.get(g),
    ]);
  }, [filtered]);

  const flashSaved = useCallback(() => {
    setSaveState("saved");
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveState("idle"), 1600);
  }, []);

  // Debounced autosave for title + body.
  const queueAutosave = useCallback(
    (id) => {
      setSaveState("saving");
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        const patch = draftRef.current;
        try {
          const updated = await notesApi.update(id, {
            title: patch.title || "",
            body: patch.body || "",
          });
          setNotes((prev) =>
            sortNotes(
              prev.map((n) => (n.id === id ? { ...n, ...updated } : n)),
            ),
          );
          flashSaved();
        } catch (e) {
          console.error("Autosave failed", e);
          setSaveState("idle");
        }
      }, 700);
    },
    [flashSaved],
  );

  // Keep a live draft mirror of the selected note's title/body (own notes only).
  useEffect(() => {
    if (selected && !selected.shared)
      draftRef.current = {
        title: selected.title || "",
        body: selected.body || "",
      };
    setSaveState("idle");
    clearTimeout(saveTimer.current);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTitle = (e) => {
    const title = e.target.value;
    if (!selected || selected.shared) return;
    draftRef.current = { ...draftRef.current, title };
    setNotes((prev) =>
      prev.map((n) => (n.id === selected.id ? { ...n, title } : n)),
    );
    queueAutosave(selected.id);
  };

  const handleBody = (html) => {
    if (!selected || selected.shared) return;
    draftRef.current = { ...draftRef.current, body: html };
    // local optimistic body only (no re-sort while typing)
    setNotes((prev) =>
      prev.map((n) => (n.id === selected.id ? { ...n, body: html } : n)),
    );
    queueAutosave(selected.id);
  };

  const select = (id, shared = false) => {
    setSelectedId(id);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (shared) {
          next.set("shared", String(id));
          next.delete("note");
        } else {
          next.set("note", String(id));
          next.delete("shared");
        }
        return next;
      },
      { replace: true },
    );
  };

  const createNote = async () => {
    try {
      const note = await notesApi.create({ title: "", body: "" });
      setNotes((prev) => sortNotes([note, ...prev]));
      setSelectedId(note.id);
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set("note", String(note.id));
          next.delete("shared");
          return next;
        },
        { replace: true },
      );
      setTimeout(() => titleRef.current && titleRef.current.focus(), 50);
    } catch (e) {
      console.error("Failed to create note", e);
    }
  };

  const togglePin = async () => {
    if (!selected) return;
    const next = !selected.pinned;
    setNotes((prev) =>
      sortNotes(
        prev.map((n) => (n.id === selected.id ? { ...n, pinned: next } : n)),
      ),
    );
    try {
      const updated = await notesApi.update(selected.id, { pinned: next });
      setNotes((prev) =>
        sortNotes(
          prev.map((n) => (n.id === selected.id ? { ...n, ...updated } : n)),
        ),
      );
    } catch (e) {
      console.error("Pin toggle failed", e);
    }
  };

  const setColor = async (value) => {
    if (!selected) return;
    setNotes((prev) =>
      prev.map((n) => (n.id === selected.id ? { ...n, color: value } : n)),
    );
    try {
      await notesApi.update(selected.id, { color: value });
    } catch (e) {
      console.error("Color change failed", e);
    }
  };

  // Arm (ISO string) or clear (null) the reminder; the server re-arms on change.
  const setReminder = async (iso) => {
    if (!selected || selected.shared) return;
    const id = selected.id;
    setNotes((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, remind_at: iso, reminder_fired: false } : n,
      ),
    );
    try {
      const updated = await notesApi.update(id, { remind_at: iso });
      setNotes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, ...updated } : n)),
      );
    } catch (e) {
      console.error("Reminder update failed", e);
    }
  };

  // Keep the card's "shared" indicator in sync after the modal adds/removes people.
  const applySharedCount = (id, count) => {
    setNotes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, shared_count: count } : n)),
    );
  };

  const deleteNote = async () => {
    if (!selected || selected.shared) return;
    if (!(await confirmDialog("Delete this note? This cannot be undone."))) return;
    const id = selected.id;
    const remaining = notes.filter((n) => n.id !== id);
    setNotes(remaining);
    try {
      await notesApi.remove(id);
    } catch (e) {
      console.error("Delete failed", e);
    }
    const nextSel = remaining[0] ? remaining[0].id : null;
    setSelectedId(nextSel);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (nextSel) next.set("note", String(nextSel));
        else next.delete("note");
        next.delete("shared");
        return next;
      },
      { replace: true },
    );
  };

  const isNarrow = typeof window !== "undefined" && window.innerWidth <= 860;

  // Size the two-pane row so it fills from its top down to 16px above the
  // viewport bottom — measured (not a hardcoded offset) so it stays correct
  // regardless of header height / wrapping.
  const rowRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (isNarrow || !rowRef.current) {
        setRowHeight(null);
        return;
      }
      const top = rowRef.current.getBoundingClientRect().top;
      setRowHeight(Math.max(360, Math.round(window.innerHeight - top - 16)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isNarrow]);

  return (
    <Page style={isNarrow ? {} : { paddingBottom: 16 }}>
      <style>{EDITOR_CSS}</style>
      <PageHeader
        icon={StickyNote}
        title="Notes"
        subtitle="Your private notes — only you can see these"
        actions={
          <PrimaryButton icon={Plus} onClick={createNote}>
            New note
          </PrimaryButton>
        }
      />

      <div
        ref={rowRef}
        style={{
          display: "flex",
          gap: 18,
          alignItems: "stretch",
          flexWrap: isNarrow ? "wrap" : "nowrap",
          height: isNarrow
            ? "auto"
            : rowHeight
              ? `${rowHeight}px`
              : "calc(100vh - 220px)",
          minHeight: isNarrow ? undefined : 360,
        }}
      >
        {/* ── Left: search + grouped list ── */}
        <div
          style={{
            width: isNarrow ? "100%" : 300,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: isNarrow ? 340 : "none",
          }}
        >
          <div style={{ position: "relative", marginBottom: 12 }}>
            <Search
              size={15}
              style={{
                position: "absolute",
                left: 11,
                top: "50%",
                transform: "translateY(-50%)",
                color: T.fg7,
              }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search notes…"
              style={{
                width: "100%",
                padding: "9px 12px 9px 34px",
                boxSizing: "border-box",
                border: `1px solid ${T.border}`,
                borderRadius: T.rMd,
                background: "var(--em-bg-card)",
                font: `400 13px ${T.font}`,
                color: T.fg1,
                outline: "none",
              }}
            />
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              paddingRight: 2,
            }}
          >
            {loading ? (
              <div
                style={{
                  padding: 24,
                  font: `400 13px ${T.font}`,
                  color: T.fg5,
                  textAlign: "center",
                }}
              >
                Loading…
              </div>
            ) : filtered.length === 0 && filteredShared.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  font: `400 13px ${T.font}`,
                  color: T.fg5,
                  textAlign: "center",
                }}
              >
                {query ? "No notes match your search." : "No notes yet."}
              </div>
            ) : (
              <>
                {grouped.map(([group, items]) => (
                  <div key={group}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        margin: "4px 2px 8px",
                        font: `700 10.5px ${T.font}`,
                        color: group === "Pinned" ? T.orange : T.fg6 || T.fg5,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                      }}
                    >
                      {group === "Pinned" && <Pin size={10} />}
                      {group}
                      <span
                        style={{ flex: 1, height: 1, background: T.divider }}
                      />
                    </div>
                    {items.map((n) => (
                      <NoteCard
                        key={n.id}
                        note={n}
                        selected={n.id === selectedId}
                        onClick={() => select(n.id)}
                      />
                    ))}
                  </div>
                ))}

                {/* collapsible "Shared with me" group below my own notes */}
                {filteredShared.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setSharedOpen((v) => !v)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        margin: "12px 0 8px",
                        padding: "0 2px",
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        font: `700 10.5px ${T.font}`,
                        color: T.fg6 || T.fg5,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                      }}
                    >
                      <Users size={10} />
                      Shared with me ({filteredShared.length})
                      <span
                        style={{ flex: 1, height: 1, background: T.divider }}
                      />
                      {sharedOpen ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronRight size={12} />
                      )}
                    </button>
                    {sharedOpen &&
                      filteredShared.map((n) => (
                        <NoteCard
                          key={`shared-${n.id}`}
                          note={n}
                          selected={n.id === selectedId}
                          onClick={() => select(n.id, true)}
                        />
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Right: editor pane ── */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: T.rXl,
            boxShadow: T.shadowSm,
            overflow: "hidden",
            minHeight: isNarrow ? 460 : 0,
          }}
        >
          {!selected ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 32,
              }}
            >
              <div style={{ textAlign: "center", maxWidth: 320 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: T.pill,
                    background: T.orange50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <StickyNote size={28} color={T.orange} />
                </div>
                <div
                  style={{
                    font: `600 17px ${T.font}`,
                    color: T.fg1,
                    marginBottom: 6,
                  }}
                >
                  {notes.length === 0 ? "No notes yet" : "Nothing selected"}
                </div>
                <div
                  style={{
                    font: `400 13px/1.5 ${T.font}`,
                    color: T.fg5,
                    marginBottom: 18,
                  }}
                >
                  {notes.length === 0
                    ? "Capture a thought, a to-do, or a meeting note. Only you can see these."
                    : "Pick a note from the list, or create a new one."}
                </div>
                <PrimaryButton icon={Plus} onClick={createNote}>
                  {notes.length === 0 ? "Create your first note" : "New note"}
                </PrimaryButton>
              </div>
            </div>
          ) : (
            <>
              <div style={{ padding: "18px 22px 0" }}>
                <input
                  ref={titleRef}
                  value={selected.title || ""}
                  onChange={handleTitle}
                  readOnly={isShared}
                  placeholder="Untitled note"
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    font: `700 24px/1.3 ${T.font}`,
                    color: T.fg1,
                    letterSpacing: "-0.02em",
                    padding: 0,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <DateTag iso={selected.created_at} />
                  {!isShared && selected.remind_at && (
                    <ReminderChip note={selected} />
                  )}
                  {isShared ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        font: `500 11.5px ${T.font}`,
                        color: T.fg5,
                      }}
                    >
                      <Share2 size={11} />
                      Shared by {selected.owner_name || "a colleague"} ·
                      read-only
                    </span>
                  ) : (
                    <span
                      style={{ font: `400 11.5px ${T.font}`, color: T.fg7 }}
                    >
                      Updated{" "}
                      {relTime(selected.updated_at || selected.created_at)}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  padding: "4px 22px 0",
                }}
              >
                <NoteEditor
                  noteId={`${isShared ? "shared-" : ""}${selected.id}`}
                  initialBody={selected.body}
                  onChange={handleBody}
                  readOnly={isShared}
                />
              </div>

              {/* footer toolbar — write actions only exist on my own notes */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: "12px 22px",
                  borderTop: `1px solid ${T.divider}`,
                  background: T.bgHead,
                }}
              >
                {!isShared && (
                  <>
                    <FooterBtn
                      icon={selected.pinned ? PinOff : Pin}
                      tone={selected.pinned ? "active" : "default"}
                      onClick={togglePin}
                    >
                      {selected.pinned ? "Unpin" : "Pin"}
                    </FooterBtn>

                    <ColorPicker value={selected.color} onPick={setColor} />

                    <ReminderControl note={selected} onSave={setReminder} />

                    <FooterBtn
                      icon={Share2}
                      tone={selected.shared_count > 0 ? "active" : "default"}
                      onClick={() => setShareOpen(true)}
                    >
                      {selected.shared_count > 0
                        ? `Shared (${selected.shared_count})`
                        : "Share"}
                    </FooterBtn>

                    <FooterBtn icon={Trash2} tone="danger" onClick={deleteNote}>
                      Delete
                    </FooterBtn>
                  </>
                )}

                <div style={{ flex: 1 }} />

                {!isShared && (
                  <span
                    style={{
                      font: `500 12px ${T.font}`,
                      color: saveState === "saved" ? T.success : T.fg7,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {saveState === "saving" && "Saving…"}
                    {saveState === "saved" && (
                      <>
                        <Check size={13} /> Saved
                      </>
                    )}
                  </span>
                )}
              </div>

              {shareOpen && !isShared && (
                <ShareModal
                  note={selected}
                  onClose={() => setShareOpen(false)}
                  onCountChange={(count) =>
                    applySharedCount(selected.id, count)
                  }
                />
              )}
            </>
          )}
        </div>
      </div>
    </Page>
  );
}
